import { Box, Divider, Typography, alpha, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import { PillButton } from './PillButton';
import type { StagedChangesPillProps } from './StagedChangesPill.types';
import { useDraggablePill } from './useDraggablePill';
import { borderRadius } from '../../tokens/core/border-radius/border-radius';
import { shadows } from '../../tokens/core/shadows/shadows';
import { fontWeight } from '../../tokens/core/typography/typography';
import { zIndex } from '../../tokens/core/z-index/z-index';

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  75%, 100% { transform: scale(2.2); opacity: 0; }
`;

/**
 * "You have unsaved changes" pill — fixed bottom-center, warning-tinted
 * chrome, pulsing dot + status text + outlined/contained button pair.
 *
 * Pure presentational: every behavior decision (visibility, button labels,
 * disabled state, mode switching) is owned by the consumer. Feature hooks
 * compose this with their own dirty/save/revert logic.
 */
export const StagedChangesPill = ({
  visible,
  message,
  secondaryButton,
  primaryButton,
  position = 'fixed',
  draggable = false,
  'data-testid': dataTestId,
}: StagedChangesPillProps) => {
  const theme = useTheme();
  const { offset, pillRef, dragHandleProps, isDragging } = useDraggablePill(draggable);

  if (!visible) return null;

  return (
    <Box
      ref={pillRef}
      data-testid={dataTestId}
      sx={{
        position,
        bottom: theme.spacing(6),
        left: '50%',
        // translateX(-50%) keeps the pill centered on its anchor; the drag
        // offset (0,0 until the user moves it) layers on top.
        transform: `translateX(-50%) translate(${offset.x}px, ${offset.y}px)`,
        zIndex: zIndex.sticky,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(3),
        bgcolor: 'background.default',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
        borderRadius: `${borderRadius.full}px`,
        px: 4,
        py: 2,
        boxShadow: `${shadows.xl}, 0 0 0 4px ${alpha(theme.palette.warning.main, 0.2)}`,
      }}
    >
      <Box
        {...(draggable ? dragHandleProps : {})}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          ...(draggable && {
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            userSelect: 'none',
          }),
        }}
      >
        <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              animation: `${ping} 1.5s cubic-bezier(0, 0, 0.2, 1) infinite`,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'warning.main',
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: fontWeight.medium }}>
          {message}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <PillButton button={secondaryButton} variant="outlined" />
        <PillButton button={primaryButton} variant="contained" />
      </Box>
    </Box>
  );
};
